import { useState, useEffect, useRef } from 'react'
import { x1nsService } from '@/services/x1nsService'

/**
 * Batch hook for resolving X1NS domain names for multiple addresses.
 * Uses x1nsService.batchGetPrimaryDomains() to resolve all at once,
 * leveraging the service's built-in 10-min cache.
 */
export function useAddressNames(addresses: string[]): {
  names: Record<string, string | null>
  isLoading: boolean
} {
  const [names, setNames] = useState<Record<string, string | null>>({})
  const [isLoading, setIsLoading] = useState(false)
  // Track the serialized address list to avoid unnecessary re-fetches
  const prevKeyRef = useRef<string>('')

  useEffect(() => {
    const unique = [...new Set(addresses.filter(Boolean))]
    if (unique.length === 0) {
      setNames({})
      setIsLoading(false)
      return
    }

    // Memoize on sorted address list to avoid re-fetching same set
    const key = unique.sort().join(',')
    if (key === prevKeyRef.current) return
    prevKeyRef.current = key

    let cancelled = false
    setIsLoading(true)

    x1nsService.batchGetPrimaryDomains(unique)
      .then(resultMap => {
        if (cancelled) return
        const obj: Record<string, string | null> = {}
        resultMap.forEach((domain, addr) => {
          obj[addr] = domain
        })
        setNames(obj)
        setIsLoading(false)
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => { cancelled = true }
  }, [addresses.length, addresses.join(',')])

  return { names, isLoading }
}

export default useAddressNames
